import React from 'react';

const AccentButton = ({ 
  variant = 'primary', 
  children, 
  onClick, 
  disabled = false,
  className = '',
  ...props 
}) => {
  const baseClasses = `
    inline-flex items-center justify-center
    rounded-xl
    transition-all duration-200 ease-in-out
    focus:outline-none
    disabled:opacity-40 disabled:cursor-not-allowed
    ${className}
  `;

  const variantClasses = {
    primary: `
      h-9 px-5 py-2 font-semibold text-sm tracking-tight
      bg-gradient-to-b from-[rgb(50,50,50)] to-[rgb(34,34,34)] dark:from-[rgb(50,50,50)] dark:to-[rgb(34,34,34)]
      hover:from-[rgb(61,61,61)] hover:to-[rgb(40,40,40)] dark:hover:from-[rgb(38,38,38)] dark:hover:to-[rgb(32,32,32)]
      text-white
      shadow-[0_0_0_1px_rgba(51,51,51,1),0_2px_4px_-1px_rgba(13,13,13,0.5),inset_0_0.5px_1px_rgba(255,255,255,0.15),inset_0_-1px_1.2px_0.35px_rgba(18,18,18,1)]
      hover:shadow-[0_0_0_1px_rgba(51,51,51,1),0_2px_4px_-1px_rgba(13,13,13,0.5),inset_0_0.5px_1px_rgba(255,255,255,0.15),inset_0_-1px_1.2px_0.35px_rgba(18,18,18,1)]
      dark:shadow-[0_0_0_1px_rgba(26,26,26,1),0_2px_4px_-1px_rgba(13,13,13,0.5),inset_0_0.5px_1px_rgba(255,255,255,0.15),inset_0_-1px_1.2px_0.35px_rgba(18,18,18,1)]
      dark:hover:shadow-[0_0_0_1px_rgba(26,26,26,1),0_2px_4px_-1px_rgba(13,13,13,0.5),inset_0_0.5px_1px_rgba(255,255,255,0.15),inset_0_-1px_1.2px_0.35px_rgba(18,18,18,1)]
    `,
    secondary: `
      h-9 px-5 py-2 font-semibold text-sm tracking-tight
      bg-gradient-to-b from-[rgb(229,229,229)] to-[rgb(226,226,226)] dark:from-[rgb(200,200,200)] dark:to-[rgb(139,135,135)]
      hover:from-[rgb(242,242,242)] hover:to-[rgb(239,239,239)] dark:hover:from-[rgb(163,163,163)] dark:hover:to-[rgb(139,135,135)]
      text-[rgb(18,18,18)]
      shadow-[0_0_0_1px_rgba(212,212,212,1),0_3px_4px_-1px_rgba(0,0,0,0.15),inset_0_1px_0_rgba(255,255,255,0.33)]
      hover:shadow-[0_0_0_1px_rgba(212,212,212,1),0_3px_4px_-1px_rgba(0,0,0,0.15),inset_0_1px_0_rgba(255,255,255,0.33)]
      dark:shadow-[0_0_0_1px_rgba(67,67,67,1),0_3px_4px_-1px_rgba(0,0,0,0.15),inset_0_1px_0_rgba(255,255,255,0.4)]
      dark:hover:shadow-[0_0_0_1px_rgba(67,67,67,1),0_3px_4px_-1px_rgba(0,0,0,0.15),inset_0_1px_0_rgba(255,255,255,0.4)]
    `,
    ghost: `
      h-9 px-3 py-[10px]
      bg-transparent
      border border-[#ECECEC] dark:border-[#202020]
      hover:bg-[#F7F7F7] dark:hover:bg-[#252525]
      text-[#121212] dark:text-[#D0D0D0]
      font-medium text-xs tracking-[-0.01em]
    `,
    orange: `
      h-9 px-5 py-2 font-semibold text-sm tracking-tight
      bg-gradient-to-b from-[rgb(227,99,35)] to-[rgb(223,90,24)] dark:from-[rgb(227,99,35)] dark:to-[rgb(223,90,24)]
      hover:from-[rgb(240,113,50)] hover:to-[rgb(235,109,46)] dark:hover:from-[rgb(191,81,26)] dark:hover:to-[rgb(223,85,17)]
      text-white
      shadow-[0_0_0_1px_rgba(191,74,15,1),0_3px_4px_-1px_rgba(252,96,16,0.9),inset_0_1px_0_rgba(255,255,255,0.33)]
      hover:shadow-[0_0_0_1px_rgba(191,74,15,1),0_3px_4px_-1px_rgba(252,96,16,0.9),inset_0_1px_0_rgba(255,255,255,0.33)]
      dark:shadow-[0_0_0_1px_rgba(191,74,15,1),0_3px_4px_-1px_rgba(252,96,16,0.4),inset_0_1px_0_rgba(255,255,255,0.33)]
      dark:hover:shadow-[0_0_0_1px_rgba(191,74,15,1),0_3px_4px_-1px_rgba(252,96,16,0.4),inset_0_1px_0_rgba(255,255,255,0.33)]
    `
  };

  const combinedClasses = `${baseClasses} ${variantClasses[variant]}`.trim();

  return (
    <button
      className={combinedClasses}
      onClick={onClick}
      disabled={disabled}
      {...props}
    >
      {children}
    </button>
  );
};

const Button = ({
  variant = 'outline',
  title,
  Icon,
  onClick,
  disabled = false,
  className = '',
  ...props
}) => {
  const variantClasses = {
    outline: 'bg-[#F1F1F1] dark:bg-[#2A2A2A]',
    ghost: 'bg-hover:bg-[#F1F1F1] dark:hover:bg-[#2A2A2A]',
  };

  return (
    <button
      onClick={onClick}
      className={`flex items-center p-1 pr-3 h-[38px] rounded-xl transition-colors ${variantClasses[variant]} ${className}`}
      disabled={disabled}
      {...props}
    >
      <div className="w-8 h-8 flex items-center justify-center">
        <Icon size={16} className="text-[#7B7B7B] dark:text-[#848484]" />
      </div>
      <span className="text-sm font-medium text-[#121212] dark:text-[#D0D0D0]">{title}</span>
    </button>
  );
};

export { AccentButton, Button };
